import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ScenarioService } from './services/scenario.service';
import { PptBuilderService } from './services/ppt-builder.service';
import { StepOneComponent } from './steps/step-one/step-one.component';
import { StepTwoComponent } from './steps/step-two/step-two.component';
import { StepThreeComponent } from './steps/step-three/step-three.component';
import { StepFourComponent } from './steps/step-four/step-four.component';
import { StepFiveComponent } from './steps/step-five/step-five.component';
import { StepSixComponent } from './steps/step-six/step-six.component';
import { StepSevenComponent } from './steps/step-seven/step-seven.component';
import {
  GenerateScenariosRequest,
  GeneratedScenario,
  GenerateModuleSummariesRequest,
  GenerateExerciseDocumentRequest,
  RedTeamCritiqueRequest,
  ApplyCritiqueRequest,
  PlanPptSlidesRequest,
  PlanPptSlidesResponse,
  PlannedPptSlide,
  GeneratePptContentRequest,
  GeneratePptSlidesRequest,
  GeneratedPptContentSlide,
  GenerateCrisisImagesRequest,
  GenerateCrisisImagesResponse,
  CrisisImageGenerationResult,
  ModulePlan,
} from './models/wizard.models';
import {
  type WizardStep,
  type UploadCategory,
  type UploadedFileItem,
  type UploadSection,
  type SelectableChipOption,
  type ExerciseFormatOption,
  type ObjectiveOption,
  type IntensityOption,
  type GeneratedImagePreview,
  WIZARD_STEPS,
  DURATION_OPTIONS,
  INTENSITY_OPTIONS,
  SOCIAL_PLATFORM_OPTIONS,
  UPLOAD_SECTIONS,
  PARTICIPANT_OPTIONS,
  EXERCISE_FORMATS,
  THREAT_SEED_OPTIONS,
  STEP_FOUR_THREAT_SEEDS,
  STEP_FOUR_OBJECTIVE_OPTIONS,
  DEFAULT_OBJECTIVE_OPTIONS,
} from './wizard-config';

@Component({
  selector: 'app-advanced-mode-wizard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    StepOneComponent,
    StepTwoComponent,
    StepThreeComponent,
    StepFourComponent,
    StepFiveComponent,
    StepSixComponent,
    StepSevenComponent,
  ],
  templateUrl: './advanced-mode-wizard.component.html',
  styleUrl: './advanced-mode-wizard.component.scss',
})
export class AdvancedModeWizardComponent {
  readonly steps: WizardStep[] = WIZARD_STEPS;

  currentStepIndex = 0;
  stepThreeDragOver = false;
  stepFourDragOver = false;
  stepSixDragOver = false;
  scenarioSeedDescription = '';
  stepFourCustomObjective = '';
  selectedExerciseFormatId: string | null = null;
  customExerciseType = '';
  selectedDurationId: string | null = null;
  selectedIntensityId: string | null = null;
  moduleCount = 0;
  questionCount = 0;
  includeNewsReports = false;
  includeSocialFeedSimulation = false;
  includeInternalEmails = false;
  modulePlans: ModulePlan[] = this.createModulePlans(0);
  documentGenerated = false;
  redTeamRequested = false;
  critiqueApplied = false;
  documentGenerationError: string | null = null;
  redTeamError: string | null = null;
  pptPlanError: string | null = null;
  pptPresentationError: string | null = null;
  crisisImagesError: string | null = null;
  targetImpactLevel: string | null = 'High';
  documentPreviewContent = '';
  redTeamCritiqueContent = '';
  pptPlanGenerated = false;
  pptContentGenerated = false;
  pptFileGenerated = false;
  pptPlanSlides: PlannedPptSlide[] = [];
  pptPlanResponse: PlanPptSlidesResponse | null = null;
  pptPlanContext: PlanPptSlidesRequest['context'] | null = null;
  pptContentSlides: GeneratedPptContentSlide[] = [];
  currentPptPreviewSlideIndex = 0;
  generatedPptBlob: Blob | null = null;
  generatedPptFilename = 'crisis-exercise-presentation.pptx';
  crisisImagesGenerated = false;
  crisisImagePreviews: GeneratedImagePreview[] = [];

  // Modal de confirmación para salir del paso 7
  showBackConfirmationModal = false;
  pendingStepIndex: number | null = null;

  // Control para mostrar/ocultar formulario de logistics en paso 6
  showLogisticsForm = true;

  // Step 5 (index 4) scenario generation
  stepFourScenariosGenerated = false;
  isGeneratingScenarios = false;
  isGeneratingModulePlans = false;
  isGeneratingDocument = false;
  isRequestingRedTeamReview = false;
  isApplyingCritique = false;
  isGeneratingPptPlan = false;
  isGeneratingPptPresentation = false;
  isGeneratingCrisisImages = false;
  selectedScenarioId: string | null = null;
  scenarioGenerationError = '';
  moduleGenerationError = '';
  noFeedbackNeeded = false;
  scenariofeedbackComment = '';

  generatedScenarios: GeneratedScenario[] = [];

  readonly durationOptions: SelectableChipOption[] = DURATION_OPTIONS;

  readonly intensityOptions: IntensityOption[] = INTENSITY_OPTIONS;

  readonly socialPlatformOptions: SelectableChipOption[] =
    SOCIAL_PLATFORM_OPTIONS;

  readonly uploadSections: UploadSection[] = UPLOAD_SECTIONS;

  readonly participantOptions: SelectableChipOption[] = PARTICIPANT_OPTIONS;

  readonly exerciseFormats: ExerciseFormatOption[] = EXERCISE_FORMATS;

  readonly threatSeedOptions: SelectableChipOption[] = THREAT_SEED_OPTIONS;

  readonly stepFourThreatSeeds: SelectableChipOption[] = STEP_FOUR_THREAT_SEEDS;

  readonly stepFourObjectiveOptions: SelectableChipOption[] =
    STEP_FOUR_OBJECTIVE_OPTIONS;

  objectiveOptions: ObjectiveOption[] = DEFAULT_OBJECTIVE_OPTIONS;

  readonly uploadedFiles: Record<UploadCategory, UploadedFileItem[]> = {
    bcpFramework: [],
    bcpPlan: [],
    businessImpact: [],
  };

  incidentScenarioFiles: UploadedFileItem[] = [];
  stepFourFiles: UploadedFileItem[] = [];
  templateFiles: UploadedFileItem[] = [];
  readonly selectedParticipantIds = new Set<string>();
  readonly selectedThreatSeedIds = new Set<string>();
  readonly selectedStepFourThreatSeedIds = new Set<string>();
  readonly selectedStepFourObjectiveIds = new Set<string>();
  readonly pendingObjectiveIds = new Set<string>();
  readonly selectedSocialPlatformIds = new Set<string>();
  selectedObjectiveIds: string[] = [];
  stepFourCustomObjectives: SelectableChipOption[] = [];

  readonly profileForm;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly scenarioService: ScenarioService,
    private readonly pptBuilderService: PptBuilderService,
  ) {
    this.profileForm = this.formBuilder.group({
      setArea: [''],
      industry: [''],
      hqLocation: [''],
      employeeCount: [''],
      uploadDocument: [false],
      departmentFocus: [false],
      focusType: [''],
      departmentInvolved: [''],
      processName: [''],
      processDescription: [''],
      criticalInfrastructure: [false],
    });
  }

  get currentStep(): WizardStep {
    return this.steps[this.currentStepIndex];
  }

  get selectedDurationLabel(): string {
    return (
      this.durationOptions.find(
        (option) => option.id === this.selectedDurationId,
      )?.label ?? 'Not selected'
    );
  }

  get selectedIntensityTitle(): string {
    return (
      this.intensityOptions.find(
        (option) => option.id === this.selectedIntensityId,
      )?.title ?? 'Not selected'
    );
  }

  get primaryButtonLabel(): string {
    // Step 5 (index 4): Show loading text or "Generate"
    if (this.currentStepIndex === 4 && !this.stepFourScenariosGenerated) {
      return this.isGeneratingScenarios ? 'Analyzing threats…' : 'Generate';
    }

    // Step 6 (index 5): Show "Proceed to generate" when showing logistics form
    if (this.currentStepIndex === 5 && this.showLogisticsForm) {
      return this.isGeneratingModulePlans
        ? 'Building module plan…'
        : 'Proceed to generate';
    }

    return this.currentStepIndex === this.steps.length - 2 ? 'Next' : 'Next';
  }

  toggleThreatSeedSelection(threatSeedId: string): void {
    if (this.selectedThreatSeedIds.has(threatSeedId)) {
      this.selectedThreatSeedIds.delete(threatSeedId);
      return;
    }

    this.selectedThreatSeedIds.add(threatSeedId);
  }

  isThreatSeedSelected(threatSeedId: string): boolean {
    return this.selectedThreatSeedIds.has(threatSeedId);
  }

  async generateScenarios(): Promise<void> {
    const payload = this.buildScenariosPayload();
    console.log('📤 Sending payload to API:', payload);

    this.isGeneratingScenarios = true;
    this.scenarioGenerationError = '';

    try {
      const response =
        await this.scenarioService.generateScenariosFromAPI(payload);
      console.log('✅ Scenarios generated successfully:', response);

      if (response.length > 0) {
        this.generatedScenarios = response;
        this.selectedScenarioId = response[0].id;
        this.stepFourScenariosGenerated = true;
        return;
      }

      this.generatedScenarios = [];
      this.selectedScenarioId = null;
      this.stepFourScenariosGenerated = false;
      this.scenarioGenerationError =
        'No scenarios were generated. Please refine your inputs and try again.';
    } catch (error) {
      console.error('❌ Error generating scenarios:', error);
      this.generatedScenarios = [];
      this.selectedScenarioId = null;
      this.stepFourScenariosGenerated = false;
      this.scenarioGenerationError =
        'We encountered an issue generating your scenario. Please try again.';
    } finally {
      this.isGeneratingScenarios = false;
    }
  }

  retryScenarioGeneration(): void {
    this.scenarioGenerationError = '';
    this.generateScenarios();
  }

  async generateModulePlans(): Promise<void> {
    const payload = this.buildModuleSummariesPayload();
    console.log('📤 Sending module summaries payload to API:', payload);

    this.isGeneratingModulePlans = true;
    this.moduleGenerationError = '';

    try {
      const response =
        await this.scenarioService.generateModuleSummariesFromAPI(payload);
      console.log('✅ Module summaries generated successfully:', response);

      // Actualizar los planes de módulos con la respuesta de la API
      if (response.length > 0) {
        this.modulePlans = response.map((module) => ({
          id: `module-${module.moduleIndex || Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          title: module.title,
          timescale: module.timescale,
          summary: module.summary,
          notes: module.designerNotes || '',
          moduleIndex: module.moduleIndex,
          designerNotes: module.designerNotes,
          artifactRequirements: module.artifactRequirements,
        }));

        // Ocultar el formulario y mostrar el plan de módulos
        this.showLogisticsForm = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // No results returned
      this.moduleGenerationError =
        'No modules were generated. Please refine your scenario and try again.';
    } catch (error) {
      console.error('❌ Error generating module summaries:', error);
      this.moduleGenerationError =
        'We encountered an issue generating your modules. Please try again.';
    } finally {
      this.isGeneratingModulePlans = false;
    }
  }

  retryModuleGeneration(): void {
    this.moduleGenerationError = '';
    this.generateModulePlans();
  }

  private buildScenariosPayload(): GenerateScenariosRequest {
    // Obtener valores del formulario
    const formValues = this.profileForm.value;

    // Convertir participantes seleccionados a sus labels
    const participants = Array.from(this.selectedParticipantIds)
      .map((id) => {
        const participant = this.participantOptions.find((p) => p.id === id);
        return participant?.label || '';
      })
      .filter((label) => label !== '');

    // Convertir formatos de ejercicio seleccionados a sus títulos
    const exerciseTypes = this.selectedExerciseFormatId
      ? [
          this.exerciseFormats.find(
            (f) => f.id === this.selectedExerciseFormatId,
          )?.title || '',
        ]
      : [];

    // Obtener objetivos predefinidos seleccionados
    const predefinedObjectives = Array.from(this.selectedStepFourObjectiveIds)
      .map((id) => {
        const objective = this.stepFourObjectiveOptions.find(
          (o) => o.id === id,
        );
        return objective?.label || '';
      })
      .filter((label) => label !== '');

    // Obtener objetivos personalizados seleccionados
    const customObjectivesSelected = Array.from(
      this.selectedStepFourObjectiveIds,
    )
      .map((id) => {
        const objective = this.stepFourCustomObjectives.find(
          (o) => o.id === id,
        );
        return objective?.label || '';
      })
      .filter((label) => label !== '');

    // Combinar todos los objetivos seleccionados
    const allObjectives = [
      ...predefinedObjectives,
      ...customObjectivesSelected,
    ];

    // Obtener intensidad
    const intensity = this.selectedIntensityId
      ? this.intensityOptions.find((i) => i.id === this.selectedIntensityId)
          ?.title || 'Medium'
      : 'Medium';

    // Obtener duración
    const duration = this.selectedDurationId
      ? this.durationOptions.find((d) => d.id === this.selectedDurationId)
          ?.label || ''
      : '';

    // Obtener plataformas de redes sociales seleccionadas
    const socialPlatforms = Array.from(this.selectedSocialPlatformIds)
      .map((id) => {
        const platform = this.socialPlatformOptions.find((p) => p.id === id);
        return platform?.label || '';
      })
      .filter((label) => label !== '');

    // Construir extended context
    const extendedContextParts: string[] = [];
    if (formValues.departmentFocus && formValues.focusType) {
      extendedContextParts.push(
        `Focus: ${formValues.focusType}${formValues.departmentInvolved ? ' - ' + formValues.departmentInvolved : ''}`,
      );
    }
    if (formValues.processName) {
      extendedContextParts.push(`Process: ${formValues.processName}`);
    }
    const extendedContext = extendedContextParts.join('; ');

    return {
      orgProfile: {
        name: formValues.setArea || '',
        industry: formValues.industry || '',
        location: formValues.hqLocation || '',
        employees: parseInt(formValues.employeeCount || '0') || 0,
        isCriticalInfra: formValues.criticalInfrastructure || false,
        extendedContext: extendedContext,
      },
      departmentInfo: {
        department: formValues.departmentInvolved || '',
        process: formValues.processDescription || formValues.processName || '',
      },
      scope: {
        participants: participants,
        exerciseTypes: exerciseTypes,
      },
      objectives: allObjectives,
      customObjectives: customObjectivesSelected,
      scenario: {
        seed: this.scenarioSeedDescription || '',
      },
      logistics: {
        intensity: intensity,
        duration: duration,
        moduleCount: this.moduleCount,
        questionCount: this.questionCount,
        artifacts: {
          newsReports: this.includeNewsReports,
          socialMedia: this.includeSocialFeedSimulation,
          socialPlatforms: socialPlatforms,
          internalEmails: this.includeInternalEmails,
        },
      },
    };
  }

  private buildModuleSummariesPayload(): GenerateModuleSummariesRequest {
    // Obtener valores del formulario
    const formValues = this.profileForm.value;

    // Obtener intensidad
    const intensity = this.selectedIntensityId
      ? this.intensityOptions.find((i) => i.id === this.selectedIntensityId)
          ?.title || 'Medium'
      : 'Medium';

    // Obtener duración
    const duration = this.selectedDurationId
      ? this.durationOptions.find((d) => d.id === this.selectedDurationId)
          ?.label || '4 hours'
      : '4 hours';

    const selectedSocialPlatforms = Array.from(this.selectedSocialPlatformIds);
    let socialPlatformCursor = 0;

    // Obtener el escenario seleccionado
    const selectedScenario = this.generatedScenarios.find(
      (s) => s.id === this.selectedScenarioId,
    );

    return {
      orgProfile: {
        name: formValues.setArea || '',
        industry: formValues.industry || '',
        location: formValues.hqLocation || '',
      },
      departmentInfo: {
        department: formValues.departmentInvolved || '',
        process: formValues.processDescription || formValues.processName || '',
      },
      scenario: {
        seed: this.scenarioSeedDescription || '',
        comment: this.scenariofeedbackComment || '',
      },
      selectedScenario: {
        title: selectedScenario?.title || '',
        description: selectedScenario?.description || '',
      },
      logistics: {
        duration: duration,
        moduleCount: this.moduleCount,
        intensity: intensity,
      },
    };
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      // Ensure message is actually a string; Error.message should be string but be defensive
      if (typeof error.message === 'string' && error.message.length > 0) {
        return error.message;
      }
      return 'An unexpected error occurred. Please try again.';
    }
    if (typeof error === 'string' && error.length > 0) {
      return error;
    }
    return 'An unexpected error occurred. Please try again.';
  }

  private buildExerciseDocumentPayload(): GenerateExerciseDocumentRequest {
    const formValues = this.profileForm.value;

    // Reutilizar lógica de payloads anteriores
    const participants = Array.from(this.selectedParticipantIds)
      .map((id) => {
        const participant = this.participantOptions.find((p) => p.id === id);
        return participant?.label || '';
      })
      .filter((label) => label !== '');

    const exerciseTypes = this.selectedExerciseFormatId
      ? [
          this.exerciseFormats.find(
            (f) => f.id === this.selectedExerciseFormatId,
          )?.title || '',
        ]
      : [];

    const predefinedObjectives = Array.from(this.selectedStepFourObjectiveIds)
      .map((id) => {
        const objective = this.stepFourObjectiveOptions.find(
          (o) => o.id === id,
        );
        return objective?.label || '';
      })
      .filter((label) => label !== '');

    const customObjectivesSelected = Array.from(
      this.selectedStepFourObjectiveIds,
    )
      .map((id) => {
        const objective = this.stepFourCustomObjectives.find(
          (o) => o.id === id,
        );
        return objective?.label || '';
      })
      .filter((label) => label !== '');

    const allObjectives = [
      ...predefinedObjectives,
      ...customObjectivesSelected,
    ];

    const intensity = this.selectedIntensityId
      ? this.intensityOptions.find((i) => i.id === this.selectedIntensityId)
          ?.title || 'Medium'
      : 'Medium';

    const duration = this.selectedDurationId
      ? this.durationOptions.find((d) => d.id === this.selectedDurationId)
          ?.label || '4 hours'
      : '4 hours';

    const selectedScenario = this.generatedScenarios.find(
      (s) => s.id === this.selectedScenarioId,
    );

    // Construir extended context
    const extendedContextParts: string[] = [];
    if (formValues.departmentFocus && formValues.focusType) {
      extendedContextParts.push(
        `Focus: ${formValues.focusType}${formValues.departmentInvolved ? ' - ' + formValues.departmentInvolved : ''}`,
      );
    }
    if (formValues.processName) {
      extendedContextParts.push(`Process: ${formValues.processName}`);
    }
    if (formValues.processDescription) {
      extendedContextParts.push(
        `Description: ${formValues.processDescription}`,
      );
    }
    const extendedContext = extendedContextParts.join('; ');

    // Determinar BCP framework basado en archivos subidos
    let bcpFramework = 'ISO 22301';
    if (this.uploadedFiles.bcpFramework.length > 0) {
      bcpFramework = 'Custom BCP Framework';
    } else if (this.uploadedFiles.bcpPlan.length > 0) {
      bcpFramework = 'Custom BCP Plan';
    }

    return {
      orgProfile: {
        name: formValues.setArea || '',
        industry: formValues.industry || '',
        location: formValues.hqLocation || '',
        _extendedContext: extendedContext,
      },
      departmentInfo: {
        department: formValues.departmentInvolved || '',
        process: formValues.processDescription || formValues.processName || '',
      },
      bcp: {
        framework: bcpFramework,
      },
      scope: {
        participants: participants,
        exerciseTypes: exerciseTypes,
      },
      objectives: allObjectives,
      customObjectives: customObjectivesSelected,
      selectedScenario: {
        title: selectedScenario?.title || '',
        description: selectedScenario?.description || '',
      },
      scenarioComment: this.scenariofeedbackComment || '',
      logistics: {
        duration: duration,
        moduleCount: this.moduleCount,
        questionsPerModule: this.questionCount,
        intensity: intensity,
      },
      moduleReviews: this.modulePlans.map((module, index) => ({
        moduleIndex: module.moduleIndex || index + 1,
        title: module.title,
        timescale: module.timescale,
        summary: module.summary,
        designerNotes: module.designerNotes || module.notes || '',
        artifactRequirements: {
          newsInject: this.includeNewsReports || undefined,
          emailInject: this.includeInternalEmails || undefined,
          socialInject: this.includeSocialFeedSimulation || undefined,
        },
      })),
    };
  }

  private buildRedTeamCritiquePayload(): RedTeamCritiqueRequest {
    const formValues = this.profileForm.value;

    // Obtener objetivos
    const predefinedObjectives = Array.from(this.selectedStepFourObjectiveIds)
      .map((id) => {
        const objective = this.stepFourObjectiveOptions.find(
          (o) => o.id === id,
        );
        return objective?.label || '';
      })
      .filter((label) => label !== '');

    const customObjectivesSelected = Array.from(
      this.selectedStepFourObjectiveIds,
    )
      .map((id) => {
        const objective = this.stepFourCustomObjectives.find(
          (o) => o.id === id,
        );
        return objective?.label || '';
      })
      .filter((label) => label !== '');

    const allObjectives = [
      ...predefinedObjectives,
      ...customObjectivesSelected,
    ];

    // Obtener el escenario seleccionado
    const selectedScenario = this.generatedScenarios.find(
      (s) => s.id === this.selectedScenarioId,
    );

    // Determinar si es una amenaza de área amplia basándose en el tipo de amenaza
    const isWideAreaThreat =
      selectedScenario?.threatType?.toLowerCase().includes('natural') ||
      selectedScenario?.threatType?.toLowerCase().includes('climate') ||
      selectedScenario?.threatType?.toLowerCase().includes('disaster') ||
      false;

    // Construir module constraints desde modulePlans
    const moduleConstraints = this.modulePlans.map((module, index) => ({
      moduleNumber: module.moduleIndex || index + 1,
      timescale: module.timescale,
      direction: module.notes || module.summary,
    }));

    return {
      orgProfile: {
        name: formValues.setArea || '',
        industry: formValues.industry || '',
        location: formValues.hqLocation || '',
      },
      document: this.documentPreviewContent,
      targetLevel: this.targetImpactLevel || 'High',
      objectives: allObjectives,
      isWideAreaThreat: isWideAreaThreat,
      scenarioName: selectedScenario?.title || '',
      scenarioComment: this.scenariofeedbackComment || '',
      moduleConstraints: moduleConstraints,
      departmentInfo: {
        name: formValues.departmentInvolved || '',
        focus:
          formValues.processDescription ||
          formValues.processName ||
          formValues.focusType ||
          '',
      },
      moduleZero: {
        title: 'Pre-crisis Baseline',
        summary:
          'Normal operations, all systems operational, standard procedures in place.',
      },
    };
  }

  private buildApplyCritiquePayload(): ApplyCritiqueRequest {
    // Construir module constraints desde modulePlans
    const moduleConstraints = this.modulePlans.map((module, index) => ({
      moduleIndex: module.moduleIndex || index + 1,
      timescale: module.timescale,
      direction: module.notes || module.summary,
    }));

    return {
      document: this.documentPreviewContent,
      critique: this.redTeamCritiqueContent,
      targetLevel: this.targetImpactLevel || 'High',
      moduleConstraints: moduleConstraints,
      moduleZero: {
        title: 'Pre-Exercise Brief',
        summary:
          'Participants are briefed on the scenario context before the exercise begins.',
      },
    };
  }

  get selectedScenario() {
    return (
      this.generatedScenarios.find((s) => s.id === this.selectedScenarioId) ??
      null
    );
  }

  onStepThreeFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.appendIncidentScenarioFiles(input.files);
    input.value = '';
  }

  onStepThreeDragOver(event: DragEvent): void {
    event.preventDefault();
    this.stepThreeDragOver = true;
  }

  onStepThreeDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.stepThreeDragOver = false;
  }

  onStepThreeDrop(event: DragEvent): void {
    event.preventDefault();
    this.stepThreeDragOver = false;
    this.appendIncidentScenarioFiles(event.dataTransfer?.files ?? null);
  }

  removeIncidentScenarioFile(fileId: string): void {
    this.incidentScenarioFiles = this.incidentScenarioFiles.filter(
      (file) => file.id !== fileId,
    );
  }

  regenerateModulePlans(): void {
    // Llamar nuevamente a la API para regenerar los módulos
    this.generateModulePlans();
  }

  async generateDocument(): Promise<void> {
    // Guard against concurrent calls
    if (this.isGeneratingDocument) {
      console.warn(
        '⚠️  Document generation already in progress. Ignoring duplicate request.',
      );
      return;
    }

    const payload = this.buildExerciseDocumentPayload();
    console.log('📤 Sending exercise document payload to API:', payload);
    this.resetStepSevenState();
    this.isGeneratingDocument = true;

    try {
      const response =
        await this.scenarioService.generateExerciseDocumentFromAPI(payload);
      console.log('✅ Exercise document generated successfully:', response);

      // Actualizar el contenido del documento con la respuesta de la API
      if (response.markdown != null && response.markdown !== '') {
        this.documentPreviewContent = response.markdown;
        this.documentGenerated = true;
        this.documentGenerationError = null;
      } else {
        // Invalid payload: no markdown content in valid response
        this.documentGenerationError =
          'Document generation returned empty content. Please try again.';
        this.documentGenerated = false;
        this.documentPreviewContent = '';
      }
    } catch (error) {
      console.error('❌ Error generating exercise document:', error);
      // Clear state and show error instead of using mock content
      this.documentGenerated = false;
      this.documentPreviewContent = '';
      this.documentGenerationError = this.getErrorMessage(error);
    } finally {
      this.isGeneratingDocument = false;
    }
  }

  async requestRedTeamReview(): Promise<void> {
    if (this.isRequestingRedTeamReview) {
      return;
    }

    const payload = this.buildRedTeamCritiquePayload();
    console.log('📤 Sending red team critique payload to API:', payload);
    this.isRequestingRedTeamReview = true;

    try {
      const response =
        await this.scenarioService.requestRedTeamCritiqueFromAPI(payload);
      console.log('✅ Red team critique received successfully:', response);

      // Almacenar la crítica del red team
      if (response.critique != null && response.critique !== '') {
        this.redTeamCritiqueContent = response.critique;
        this.redTeamRequested = true;
        this.redTeamError = null;
      } else {
        // Invalid payload: no critique content in valid response
        this.redTeamError =
          'Red team critique returned empty content. Please try again.';
        this.redTeamRequested = false;
        this.redTeamCritiqueContent = '';
      }
    } catch (error) {
      console.error('❌ Error requesting red team critique:', error);
      // Clear state and show error instead of using mock content
      this.redTeamRequested = false;
      this.redTeamCritiqueContent = '';
      this.redTeamError = this.getErrorMessage(error);
    } finally {
      this.isRequestingRedTeamReview = false;
    }
  }

  async applyCritique(): Promise<void> {
    if (this.isApplyingCritique || this.critiqueApplied) {
      return;
    }

    const payload = this.buildApplyCritiquePayload();
    console.log('📤 Sending apply critique payload to API:', payload);
    this.isApplyingCritique = true;

    try {
      const response = await this.scenarioService.applyCritiqueFromAPI(payload);
      console.log('✅ Critique applied successfully:', response);

      // Actualizar el documento con las mejoras aplicadas
      if (response.document) {
        this.documentPreviewContent = response.document;
        this.critiqueApplied = true;
      } else {
        // Invalid payload: no document content in valid response
        console.warn(
          '⚠️  Critique response returned empty document. Critique not marked as applied.',
        );
        this.critiqueApplied = false;
      }
    } catch (error) {
      console.error('❌ Error applying critique:', error);
      // Do NOT mark as applied on error; allow user to retry
      this.critiqueApplied = false;
    } finally {
      this.isApplyingCritique = false;
    }
  }

  private buildPptPayload(): PlanPptSlidesRequest {
    const formValues = this.profileForm.value;
    const selectedScenario = this.generatedScenarios.find(
      (s) => s.id === this.selectedScenarioId,
    );

    const duration = this.selectedDurationId
      ? this.durationOptions.find((d) => d.id === this.selectedDurationId)
          ?.label || '4 hours'
      : '4 hours';

    const selectedSocialPlatforms = Array.from(this.selectedSocialPlatformIds);
    let socialPlatformCursor = 0;

    // Construir array de modules desde modulePlans
    const modules = this.modulePlans.map((module) => {
      // Determinar artifact types basándose en artifactRequirements
      const artifactTypes: string[] = [];
      let artifactPlatform: string | undefined;
      if (module.artifactRequirements?.newsInject) {
        artifactTypes.push('news-article');
      }
      if (module.artifactRequirements?.emailInject) {
        artifactTypes.push('email-inject');
      }
      if (module.artifactRequirements?.socialInject) {
        artifactTypes.push('social-media');
        if (selectedSocialPlatforms.length > 0) {
          artifactPlatform =
            selectedSocialPlatforms[
              socialPlatformCursor % selectedSocialPlatforms.length
            ];
          socialPlatformCursor += 1;
        }
      }

      return {
        moduleIndex: module.moduleIndex || 0,
        title: module.title,
        timescale: module.timescale,
        summary: module.summary,
        artifactTypes: artifactTypes,
        artifactPlatform: artifactPlatform,
      };
    });

    // Construir moduleContentHints desde modulePlans
    const moduleContentHints = this.modulePlans.map((module) => ({
      moduleIndex: module.moduleIndex || 0,
      title: module.title,
      timescale: module.timescale,
      contentBrief: module.summary,
    }));

    // Recopilar todos los artifact types únicos
    const uniqueArtifactTypes = new Set<string>();
    modules.forEach((mod) => {
      mod.artifactTypes.forEach((type) => {
        if (type === 'news-article') uniqueArtifactTypes.add('news');
        else if (type === 'email-inject') uniqueArtifactTypes.add('email');
        else if (type === 'social-media') {
          if (mod.artifactPlatform) {
            uniqueArtifactTypes.add(`social:${mod.artifactPlatform}`);
          } else {
            uniqueArtifactTypes.add('social');
          }
        }
      });
    });

    return {
      document: this.documentPreviewContent,
      context: {
        organization: {
          name: formValues.setArea || '',
          industry: formValues.industry || '',
          location: formValues.hqLocation || '',
        },
        selectedScenario: {
          title: selectedScenario?.title || '',
          threatType: selectedScenario?.threatType || '',
          description: selectedScenario?.description || '',
        },
        exerciseScope: {
          participants: Array.from(this.selectedParticipantIds).map(
            (participantId) =>
              this.participantOptions.find((p) => p.id === participantId)
                ?.label || participantId,
          ),
        },
        objectives: this.getSelectedObjectiveLabels(),
        logistics: {
          duration: duration,
          moduleCount: this.moduleCount,
          intensity: this.selectedIntensityTitle,
        },
        modules: modules,
        moduleContentHints: moduleContentHints,
        artifactTypes: Array.from(uniqueArtifactTypes),
      },
    };
  }

  private buildPptContentPayload(
    planResponse: PlanPptSlidesResponse | PlannedPptSlide[],
    context: PlanPptSlidesRequest['context'],
  ): GeneratePptContentRequest {
    const slidePlan = this.parseSlidePlan(planResponse);

    return {
      document: this.documentPreviewContent,
      slidePlan,
      context,
    };
  }

  private buildPptSlidesPayload(
    slides: GeneratedPptContentSlide[],
    context: PlanPptSlidesRequest['context'],
  ): GeneratePptSlidesRequest {
    return {
      document: this.documentPreviewContent,
      slides,
      context,
    };
  }

  private getSelectedObjectiveLabels(): string[] {
    const selectedObjectiveLabels = Array.from(
      this.selectedStepFourObjectiveIds,
    )
      .map(
        (objectiveId) =>
          this.stepFourObjectiveOptions.find((o) => o.id === objectiveId)
            ?.label,
      )
      .filter((label): label is string => !!label);

    const customObjectiveLabels = this.stepFourCustomObjectives.map(
      (objective) => objective.label,
    );

    return [...selectedObjectiveLabels, ...customObjectiveLabels];
  }

  private parseSlidePlan(
    planResponse: PlanPptSlidesResponse | unknown,
  ): PlannedPptSlide[] {
    if (Array.isArray(planResponse)) {
      return this.normalizePlannedSlides(planResponse);
    }

    if (!planResponse || typeof planResponse !== 'object') {
      return [];
    }

    const response = planResponse as { slidePlan?: PlannedPptSlide[] | string };
    const slidePlan = response.slidePlan;

    if (Array.isArray(slidePlan)) {
      return this.normalizePlannedSlides(slidePlan);
    }

    if (typeof slidePlan === 'string') {
      try {
        const parsed = JSON.parse(slidePlan);
        return Array.isArray(parsed) ? this.normalizePlannedSlides(parsed) : [];
      } catch {
        return [];
      }
    }

    return [];
  }

  private normalizePlannedSlides(rawSlides: unknown[]): PlannedPptSlide[] {
    return rawSlides
      .filter(
        (slide): slide is Record<string, unknown> =>
          !!slide && typeof slide === 'object',
      )
      .map((slide, index) => ({
        slideNumber:
          typeof slide['slideNumber'] === 'number'
            ? slide['slideNumber']
            : index + 1,
        title: typeof slide['title'] === 'string' ? slide['title'] : '',
        layout:
          typeof slide['layout'] === 'string' ? slide['layout'] : 'content',
        purpose: typeof slide['purpose'] === 'string' ? slide['purpose'] : '',
        keyPoints: this.normalizeSlideKeyPoints(slide['keyPoints']),
        speakerNotes:
          typeof slide['speakerNotes'] === 'string'
            ? slide['speakerNotes']
            : '',
        moduleNumber:
          typeof slide['moduleNumber'] === 'number' ||
          slide['moduleNumber'] === null
            ? slide['moduleNumber']
            : null,
        artifactPlatform:
          typeof slide['artifactPlatform'] === 'string' ||
          slide['artifactPlatform'] === null
            ? slide['artifactPlatform']
            : null,
      }));
  }

  private normalizeSlideKeyPoints(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => this.keyPointToText(item))
      .filter((item): item is string => item.length > 0);
  }

  private keyPointToText(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (value && typeof value === 'object') {
      const keyPointObject = value as Record<string, unknown>;
      const phaseTitle =
        typeof keyPointObject['phaseTitle'] === 'string'
          ? keyPointObject['phaseTitle'].trim()
          : '';
      const description =
        typeof keyPointObject['description'] === 'string'
          ? keyPointObject['description'].trim()
          : '';

      if (phaseTitle && description) {
        return `${phaseTitle}: ${description}`;
      }

      if (phaseTitle) {
        return phaseTitle;
      }

      if (description) {
        return description;
      }

      try {
        const serialized = JSON.stringify(keyPointObject);
        return serialized === '{}' ? '' : serialized;
      } catch {
        return '';
      }
    }

    return '';
  }

  private downloadPresentation(blob: Blob, filename: string): void {
    if (!blob || blob.size === 0) {
      console.error('Cannot download presentation: empty blob');
      return;
    }

    const normalizedFilename = filename.toLowerCase().endsWith('.pptx')
      ? filename
      : `${filename}.pptx`;

    const navigatorWithMsSave = navigator as Navigator & {
      msSaveOrOpenBlob?: (blobPart: Blob, defaultName?: string) => boolean;
    };

    if (typeof navigatorWithMsSave.msSaveOrOpenBlob === 'function') {
      navigatorWithMsSave.msSaveOrOpenBlob(blob, normalizedFilename);
      return;
    }

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = normalizedFilename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();

    // Revoke asynchronously so browser has time to start the file transfer.
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
      if (anchor.parentNode) {
        anchor.parentNode.removeChild(anchor);
      }
    }, 1500);
  }

  async generatePowerPointPlan(): Promise<void> {
    if (this.isGeneratingPptPlan) {
      console.warn(
        '⚠️  PowerPoint plan generation already in progress. Ignoring duplicate request.',
      );
      return;
    }

    const planPayload = this.buildPptPayload();
    console.log('📤 Sending PPT slides plan payload to API:', planPayload);
    this.isGeneratingPptPlan = true;

    this.pptPlanGenerated = false;
    this.pptContentGenerated = false;
    this.pptFileGenerated = false;
    this.pptPlanSlides = [];
    this.pptPlanResponse = null;
    this.pptPlanContext = null;
    this.pptContentSlides = [];
    this.currentPptPreviewSlideIndex = 0;
    this.generatedPptBlob = null;
    this.generatedPptFilename = 'crisis-exercise-presentation.pptx';
    this.pptPlanError = null;

    try {
      const planResponse =
        await this.scenarioService.planPptSlidesFromAPI(planPayload);
      console.log('✅ PPT slides plan generated successfully:', planResponse);

      this.pptPlanResponse = planResponse;
      const plannedSlides = this.parseSlidePlan(planResponse);
      this.pptPlanSlides = plannedSlides;
      this.pptPlanContext = planPayload.context;

      this.pptPlanGenerated = true;
    } catch (error) {
      console.error('❌ Error generating PowerPoint plan:', error);
      // Clear state and show error instead of using fallback content
      this.pptPlanGenerated = false;
      this.pptPlanSlides = [];
      this.pptPlanResponse = null;
      this.pptPlanContext = null;
      this.pptPlanError = this.getErrorMessage(error);
    } finally {
      this.isGeneratingPptPlan = false;
    }
  }

  async generatePowerPointPresentation(): Promise<void> {
    if (
      !this.pptPlanGenerated ||
      !this.pptPlanContext ||
      this.isGeneratingPptPresentation
    ) {
      return;
    }

    this.isGeneratingPptPresentation = true;
    this.pptContentGenerated = false;
    this.pptFileGenerated = false;
    this.pptContentSlides = [];
    this.currentPptPreviewSlideIndex = 0;
    this.generatedPptBlob = null;
    this.generatedPptFilename = 'crisis-exercise-presentation.pptx';
    this.pptPresentationError = null;

    try {
      const planSourceForContent = this.pptPlanResponse ?? this.pptPlanSlides;
      const contentPayload = this.buildPptContentPayload(
        planSourceForContent,
        this.pptPlanContext,
      );
      console.log('📤 Sending PPT content payload to API:', contentPayload);

      const contentResponse =
        await this.scenarioService.generatePptContentFromAPI(contentPayload);
      console.log('✅ PPT content generated successfully:', contentResponse);

      this.pptContentSlides = contentResponse;
      this.pptContentGenerated = true;
      this.currentPptPreviewSlideIndex = 0;

      const slidesPayload = this.buildPptSlidesPayload(
        this.pptContentSlides,
        this.pptPlanContext,
      );
      console.log('📤 Sending PPT render payload to API:', slidesPayload);

      const presentation =
        await this.scenarioService.generatePptSlidesFromAPI(slidesPayload);
      console.log('✅ PPT file generated successfully:', presentation.filename);

      this.generatedPptBlob = presentation.blob;
      this.generatedPptFilename = presentation.filename;
      this.downloadPresentation(presentation.blob, presentation.filename);
      this.pptFileGenerated = true;
    } catch (error) {
      console.error('❌ Error generating PowerPoint presentation:', error);
      // Clear state and show error instead of using fallback content
      this.pptContentGenerated = false;
      this.pptFileGenerated = false;
      this.pptContentSlides = [];
      this.currentPptPreviewSlideIndex = 0;
      this.generatedPptBlob = null;
      this.pptPresentationError = this.getErrorMessage(error);
    } finally {
      this.isGeneratingPptPresentation = false;
    }
  }

  async downloadPptPresentation(): Promise<void> {
    if (this.generatedPptBlob) {
      this.downloadPresentation(
        this.generatedPptBlob,
        this.generatedPptFilename,
      );
      return;
    }

    if (this.pptContentSlides.length === 0) return;

    const orgName =
      this.pptPlanContext?.organization?.name ?? 'crisis-exercise';
    const filename = `${orgName.toLowerCase().replace(/\s+/g, '-')}-exercise`;
    await this.pptBuilderService.downloadPresentation(
      this.pptContentSlides,
      filename,
    );
  }

  private buildCrisisImagesPayload(): GenerateCrisisImagesRequest {
    const selectedScenario = this.generatedScenarios.find(
      (s) => s.id === this.selectedScenarioId,
    );

    // Construir array de modules desde modulePlans
    const modules = this.modulePlans.map((module) => ({
      moduleIndex: module.moduleIndex || 0,
      moduleTitle: module.title,
      timescale: module.timescale,
      moduleContext: module.summary,
    }));

    return {
      generationScope: 'modules',
      selectedScenario: {
        title: selectedScenario?.title || '',
        description: selectedScenario?.description || '',
      },
      modules: modules,
    };
  }

  async generateCrisisImages(): Promise<void> {
    if (this.isGeneratingCrisisImages) {
      console.warn(
        '⚠️  Crisis images generation already in progress. Ignoring duplicate request.',
      );
      return;
    }

    const payload = this.buildCrisisImagesPayload();
    console.log('📤 Sending crisis images payload to API:', payload);

    this.isGeneratingCrisisImages = true;
    this.crisisImagesGenerated = false;
    this.crisisImagePreviews = [];
    this.crisisImagesError = null;

    try {
      const response =
        await this.scenarioService.generateCrisisImagesFromAPI(payload);
      console.log('✅ Crisis images generated successfully:', response);

      this.crisisImagePreviews = this.buildCrisisImagePreviews(response);
      this.crisisImagesGenerated = this.crisisImagePreviews.length > 0;
    } catch (error) {
      console.error('❌ Error generating crisis images:', error);
      // Clear state and show error instead of using fallback content
      this.crisisImagesGenerated = false;
      this.crisisImagePreviews = [];
      this.crisisImagesError = this.getErrorMessage(error);
    } finally {
      this.isGeneratingCrisisImages = false;
    }
  }

  private buildCrisisImagePreviews(
    response: GenerateCrisisImagesResponse,
  ): GeneratedImagePreview[] {
    const successfulResults = response.results.filter(
      (result): result is CrisisImageGenerationResult & { imageData: string } =>
        result.status === 'success' && !!result.imageData,
    );

    return successfulResults.map((result, index) => {
      const mimeType = result.mimeType || 'image/jpeg';
      const extension = this.getImageExtensionFromMimeType(mimeType);
      const moduleLabel =
        typeof result.moduleIndex === 'number'
          ? `module-${result.moduleIndex}`
          : `image-${index + 1}`;
      const filename = `${moduleLabel}.${extension}`;
      const title = result.title || result.moduleTitle || moduleLabel;

      return {
        id: `${moduleLabel}-${index}`,
        title,
        moduleIndex: result.moduleIndex,
        mimeType,
        src: `data:${mimeType};base64,${result.imageData}`,
        filename,
      };
    });
  }

  downloadCrisisImage(preview: GeneratedImagePreview): void {
    const base64Payload = preview.src.split(',')[1] ?? '';
    const binaryString = atob(base64Payload);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: preview.mimeType });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = preview.filename;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }

  private getImageExtensionFromMimeType(mimeType: string): string {
    switch (mimeType) {
      case 'image/png':
        return 'png';
      case 'image/webp':
        return 'webp';
      case 'image/gif':
        return 'gif';
      default:
        return 'jpg';
    }
  }

  private createModulePlans(variantIndex: number): ModulePlan[] {
    const defaultModules = [
      {
        moduleIndex: 1,
        title: 'Module 1 — Early Warning Signs',
        timescale: 'T-30 days',
        summary: 'Telemetry anomalies appear across refrigerated shipments.',
        designerNotes: 'Introduce supplier uncertainty early.',
        artifactRequirements: { newsInject: true },
      },
      {
        moduleIndex: 2,
        title: 'Module 2 — Initial Disruption',
        timescale: 'T+0h',
        summary:
          'A critical shipment arrives with compromised temperature records.',
        designerNotes: 'Force escalation and legal review.',
        artifactRequirements: { emailInject: true },
      },
      {
        moduleIndex: 3,
        title: 'Module 3 — Cascading Impacts',
        timescale: 'T+6h',
        summary: 'More distribution data appears unreliable across regions.',
        designerNotes: '',
        artifactRequirements: { socialInject: true },
      },
      {
        moduleIndex: 4,
        title: 'Module 4 — Regulatory Scrutiny',
        timescale: 'T+24h',
        summary:
          'Public and regulatory scrutiny intensifies as disruption spreads.',
        designerNotes: 'End with executive decision pressure.',
        artifactRequirements: {},
      },
    ];

    return defaultModules.map((module) => ({
      id: `module-plan-${variantIndex}-${module.moduleIndex}`,
      title: module.title,
      timescale: module.timescale,
      summary: module.summary,
      notes: module.designerNotes,
      moduleIndex: module.moduleIndex,
      designerNotes: module.designerNotes,
      artifactRequirements: module.artifactRequirements,
    }));
  }

  private appendIncidentScenarioFiles(files: FileList | null): void {
    const nextItems = this.createUploadedFileItems('incident-scenario', files);

    if (!nextItems.length) {
      return;
    }

    this.incidentScenarioFiles = [...this.incidentScenarioFiles, ...nextItems];
  }

  private replaceTemplateFiles(files: FileList | null): void {
    const nextItems = this.createUploadedFileItems('template', files);

    if (!nextItems.length) {
      return;
    }

    this.templateFiles = [nextItems[0]];
  }

  private createUploadedFileItems(
    prefix: string,
    files: FileList | null,
  ): UploadedFileItem[] {
    if (!files?.length) {
      return [];
    }

    return Array.from(files).map((file, index) => ({
      id: `${prefix}-${file.name}-${file.size}-${Date.now()}-${index}`,
      name: file.name,
      sizeLabel: this.formatFileSize(file.size),
    }));
  }

  private formatFileSize(sizeInBytes: number): string {
    if (!sizeInBytes) {
      return '0 KB';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let value = sizeInBytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const roundedValue =
      value >= 10 || unitIndex === 0
        ? Math.round(value)
        : Number(value.toFixed(1));
    return `${roundedValue} ${units[unitIndex]}`;
  }

  previous(): void {
    if (this.currentStepIndex === 0) {
      void this.router.navigate(['/']);
      return;
    }

    // Si está en el paso 7 (índice 6), mostrar modal de confirmación
    if (this.currentStepIndex === 6) {
      this.pendingStepIndex = this.currentStepIndex - 1;
      this.showBackConfirmationModal = true;
      return;
    }

    this.currentStepIndex -= 1;

    // Si navega al paso 6, resetear el formulario de logistics
    if (this.currentStepIndex === 5) {
      this.showLogisticsForm = true;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  next(): void {
    // If in step 5 and scenarios haven't been generated, generate them instead of advancing
    if (this.currentStepIndex === 4 && !this.stepFourScenariosGenerated) {
      this.generateScenarios();
      return;
    }

    // If in step 6 and showing logistics form, generate module plans
    if (this.currentStepIndex === 5 && this.showLogisticsForm) {
      this.generateModulePlans();
      return;
    }

    if (this.currentStepIndex < this.steps.length - 1) {
      this.currentStepIndex += 1;

      // Si navega al paso 6, resetear el formulario de logistics
      if (this.currentStepIndex === 5) {
        this.showLogisticsForm = true;
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToStep(index: number): void {
    // Si está en el paso 7 (índice 6) e intenta ir a otro paso, mostrar modal
    if (this.currentStepIndex === 6 && index !== 6) {
      this.pendingStepIndex = index;
      this.showBackConfirmationModal = true;
      return;
    }

    this.currentStepIndex = index;

    // Si navega al paso 6, resetear el formulario de logistics
    if (index === 5) {
      this.showLogisticsForm = true;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Confirmar salida del paso 7
  confirmBackToProcess(): void {
    if (this.pendingStepIndex !== null) {
      this.currentStepIndex = this.pendingStepIndex;
      this.showBackConfirmationModal = false;
      this.pendingStepIndex = null;

      // Resetear el estado del paso 7
      this.resetStepSevenState();

      // Si navega al paso 6, resetear el formulario de logistics
      if (this.currentStepIndex === 5) {
        this.showLogisticsForm = true;
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Cancelar salida del paso 7
  cancelBackToProcess(): void {
    this.showBackConfirmationModal = false;
    this.pendingStepIndex = null;
  }

  private resetStepSevenState(): void {
    this.documentGenerated = false;
    this.redTeamRequested = false;
    this.critiqueApplied = false;
    this.documentGenerationError = null;
    this.redTeamError = null;
    this.targetImpactLevel = 'High';
    this.documentPreviewContent = '';
    this.redTeamCritiqueContent = '';

    this.pptPlanGenerated = false;
    this.pptContentGenerated = false;
    this.pptFileGenerated = false;
    this.pptPlanSlides = [];
    this.pptPlanResponse = null;
    this.pptPlanContext = null;
    this.pptContentSlides = [];
    this.currentPptPreviewSlideIndex = 0;
    this.generatedPptBlob = null;
    this.generatedPptFilename = 'crisis-exercise-presentation.pptx';
    this.pptPlanError = null;
    this.pptPresentationError = null;

    this.crisisImagesGenerated = false;
    this.crisisImagePreviews = [];
    this.crisisImagesError = null;

    this.isGeneratingDocument = false;
    this.isRequestingRedTeamReview = false;
    this.isApplyingCritique = false;
    this.isGeneratingPptPlan = false;
    this.isGeneratingPptPresentation = false;
    this.isGeneratingCrisisImages = false;
  }
}
